import * as React from "react"
import { useDispatch, useSelector } from "react-redux"
import { addToast, updateToast, dismissToast, removeToast, ToasterToast } from "@/lib/store/layout"
import type { AppDispatch, RootState } from "@/lib/store"

const TOAST_REMOVE_DELAY = 1000

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

type Toast = Omit<ToasterToast, "id">

let globalDispatch: AppDispatch

function toast({ ...props }: Toast) {
  const id = genId()

  const update = (props: ToasterToast) => globalDispatch(updateToast({ ...props, id }))
  const dismiss = () => globalDispatch(dismissToast({ toastId: id }))

  globalDispatch(
    addToast({
      duration: 2500,
      ...props,
      id,
      open: true,
    })
  )

  return {
    id: id,
    dismiss,
    update,
  }
}

function useToast() {
  const dispatch = useDispatch<AppDispatch>()
  const { toasts } = useSelector((state: RootState) => state.layout)
  globalDispatch = dispatch

  React.useEffect(() => {
    const toastsWithOpenFalse = toasts.filter((toast) => !toast.open)

    if (toastsWithOpenFalse.length) {
      const timeouts = new Map<string, ReturnType<typeof setTimeout>>()

      toastsWithOpenFalse.forEach((toast) => {
        if (!toast.id) return
        const timeout = setTimeout(() => {
          dispatch(removeToast({ toastId: toast.id }))
          timeouts.delete(toast.id!)
        }, TOAST_REMOVE_DELAY)
        timeouts.set(toast.id, timeout)
      })

      return () => {
        timeouts.forEach((timeout) => clearTimeout(timeout))
      }
    }
  }, [toasts, dispatch])

  return {
    toasts,
    toast,
    dismiss: (toastId?: string) => dispatch(dismissToast({ toastId })),
  }
}

export { useToast, toast }
